import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../Firebase';
import { CreditCard, Plus, CheckCircle, XCircle, Search, TrendingUp, Users, AlertCircle } from 'lucide-react';
import etuBg from './etu2.jpg';

const MONTANTS_INSCRIPTION  = 50000; // Fixe pour tous les niveaux
const MONTANTS_MENSUEL      = { A1: 35000, A2: 38000, B1: 42000, B2: 45000, C1: 50000, C2: 55000 };
const MODES = ['Wave','Orange Money','Virement','Espèces'];

export default function AdminFinances({ staff }) {
  const [etudiants, setEtudiants]   = useState([]);
  const [paiements, setPaiements]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('apercu');
  const [search, setSearch]         = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState({
    etudiantId: '', type: 'mensuel', montant: '', mode: 'Wave', mois: '', statut: 'payé', note: ''
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    const etSnap  = await getDocs(collection(db, 'etudiants'));
    const etData  = etSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => !e.role || e.role === 'etudiant');
    setEtudiants(etData);
    const paySnap = await getDocs(collection(db, 'paiements'));
    const payData = paySnap.docs.map(d => ({ id: d.id, ...d.data() }));
    payData.sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
    setPaiements(payData);
    setLoading(false);
  }

  async function handleConfirm(paiement) {
    if (!window.confirm(`Confirmer le paiement de ${paiement.etudiantNom} et activer son compte ?`)) return;
    // Confirme le paiement
    await updateDoc(doc(db, 'paiements', paiement.id), {
      statut: 'payé',
      confirméPar: staff?.nom,
      confirméLe: serverTimestamp(),
    });
    // Active le compte étudiant
    await updateDoc(doc(db, 'etudiants', paiement.etudiantId), {
      actif: true,
      paiementStatut: 'confirmé',
    });
    setMsg(`Compte de ${paiement.etudiantNom} activé avec succès.`);
    await fetchData();
    setTimeout(() => setMsg(''), 3000);
  }

  async function handleReject(paiement) {
    if (!window.confirm(`Rejeter le paiement de ${paiement.etudiantNom} ?`)) return;
    await updateDoc(doc(db, 'paiements', paiement.id), {
      statut: 'annulé',
      rejétéPar: staff?.nom,
    });
    setMsg(`Paiement de ${paiement.etudiantNom} rejeté.`);
    await fetchData();
    setTimeout(() => setMsg(''), 3000);
  }

  async function handleAdd() {
    setSaving(true);
    const et = etudiants.find(e => e.id === form.etudiantId);
    const id = `${form.etudiantId}_${form.type}_${Date.now()}`;
    await setDoc(doc(db, 'paiements', id), {
      ...form,
      montant:       parseInt(form.montant),
      etudiantNom:   et?.nom || '',
      etudiantEmail: et?.email || '',
      niveau:        et?.niveau || 'A1',
      createdAt:     serverTimestamp(),
      createdBy:     staff?.uid,
    });
    setMsg('Paiement enregistré.');
    setShowForm(false);
    setForm({ etudiantId:'', type:'mensuel', montant:'', mode:'Wave', mois:'', statut:'payé', note:'' });
    await fetchData();
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  }

  // Stats
  const inscriptions = paiements.filter(p => p.type === 'inscription');
  const mensuels     = paiements.filter(p => p.type === 'mensuel' || !p.type);
  const totalInscriptions = inscriptions.filter(p => p.statut === 'payé').reduce((s,p) => s+(p.montant||0), 0);
  const totalMensuels     = mensuels.filter(p => p.statut === 'payé').reduce((s,p) => s+(p.montant||0), 0);
  const totalGlobal       = totalInscriptions + totalMensuels;

  const filtered = paiements.filter(p =>
    (!search || p.etudiantNom?.toLowerCase().includes(search.toLowerCase()) || p.etudiantEmail?.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredByTab = activeTab === 'inscriptions' ? filtered.filter(p => p.type === 'inscription')
                      : activeTab === 'mensuels'      ? filtered.filter(p => p.type === 'mensuel' || !p.type)
                      : filtered;

  if (loading) return <div className="admin-page-loading"><div className="admin-spinner"/></div>;

  return (
    <div className="admin-page admin-page-bg admin-page-bg--finances"
      style={{backgroundImage:`url(${etuBg})`,backgroundSize:'cover',backgroundPosition:'center',position:'relative'}}>

      <div className="admin-page__header">
        <div>
          <h2>Finances</h2>
          <p>Gestion des inscriptions et paiements mensuels</p>
        </div>
        <div className="admin-page__actions">
          <button className="admin-btn admin-btn--primary" onClick={() => setShowForm(!showForm)}>
            <Plus size={15}/> Enregistrer un paiement
          </button>
        </div>
      </div>

      {msg && <div className="admin-msg admin-msg--ok">{msg}</div>}

      {/* Alerte paiements en attente */}
      {paiements.filter(p => p.statut === 'en attente').length > 0 && (
        <div style={{
          background:'rgba(245,158,11,0.08)',
          border:'1.5px solid rgba(245,158,11,0.3)',
          borderRadius:10,
          padding:'0.85rem 1.25rem',
          marginBottom:'1.5rem',
          display:'flex',
          alignItems:'center',
          gap:'0.75rem',
        }}>
          <AlertCircle size={20} color="#b45309"/>
          <div>
            <strong style={{color:'#b45309',fontSize:'0.9rem'}}>
              {paiements.filter(p => p.statut === 'en attente').length} paiement(s) en attente de confirmation
            </strong>
            <p style={{fontSize:'0.82rem',color:'#92400e',marginTop:2}}>
              Vérifiez votre Wave et confirmez les paiements pour activer les comptes étudiants.
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="admin-stat-grid" style={{gridTemplateColumns:'repeat(3,1fr)',marginBottom:'1.5rem'}}>
        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{background:'rgba(27,43,94,0.08)'}}>
            <TrendingUp size={22} color="#1B2B5E"/>
          </div>
          <div>
            <span className="admin-stat-card__val">{totalGlobal.toLocaleString('fr-FR')} F</span>
            <span className="admin-stat-card__label">Total encaissé</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{background:'rgba(47,158,68,0.08)'}}>
            <Users size={22} color="#2f9e44"/>
          </div>
          <div>
            <span className="admin-stat-card__val">{totalInscriptions.toLocaleString('fr-FR')} F</span>
            <span className="admin-stat-card__label">Frais d'inscription</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{background:'rgba(201,168,76,0.1)'}}>
            <CreditCard size={22} color="#C9A84C"/>
          </div>
          <div>
            <span className="admin-stat-card__val">{totalMensuels.toLocaleString('fr-FR')} F</span>
            <span className="admin-stat-card__label">Paiements mensuels</span>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="admin-form-box" style={{marginBottom:'1.5rem'}}>
          <h3><CreditCard size={16}/> Nouveau paiement</h3>
          <div className="admin-form-grid">
            <div className="admin-form-field">
              <label>Type de paiement</label>
              <select value={form.type} onChange={e => {
                const type = e.target.value;
                const et = etudiants.find(x => x.id === form.etudiantId);
                const montant = type === 'inscription'
                  ? MONTANTS_INSCRIPTION
                  : (MONTANTS_MENSUEL[et?.niveau||'A1'] || 35000);
                setForm(p => ({...p, type, montant: montant.toString()}));
              }}>
                <option value="inscription">Frais d'inscription</option>
                <option value="mensuel">Paiement mensuel</option>
              </select>
            </div>
            <div className="admin-form-field">
              <label>Étudiant</label>
              <select value={form.etudiantId} onChange={e => {
                const et = etudiants.find(x => x.id === e.target.value);
                const montant = form.type === 'inscription'
                  ? MONTANTS_INSCRIPTION
                  : (MONTANTS_MENSUEL[et?.niveau||'A1'] || 35000);
                setForm(p => ({...p, etudiantId: e.target.value, montant: montant.toString()}));
              }}>
                <option value="">Sélectionner...</option>
                {etudiants.map(e => <option key={e.id} value={e.id}>{e.nom} — {e.niveau}</option>)}
              </select>
            </div>
            <div className="admin-form-field">
              <label>Montant (FCFA)</label>
              <input type="number" value={form.montant} onChange={e => setForm(p => ({...p, montant: e.target.value}))}/>
            </div>
            <div className="admin-form-field">
              <label>Mode de paiement</label>
              <select value={form.mode} onChange={e => setForm(p => ({...p, mode: e.target.value}))}>
                {MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {form.type === 'mensuel' && (
              <div className="admin-form-field">
                <label>Mois concerné</label>
                <input type="month" value={form.mois} onChange={e => setForm(p => ({...p, mois: e.target.value}))}/>
              </div>
            )}
            <div className="admin-form-field">
              <label>Statut</label>
              <select value={form.statut} onChange={e => setForm(p => ({...p, statut: e.target.value}))}>
                <option value="payé">Payé</option>
                <option value="en attente">En attente</option>
                <option value="annulé">Annulé</option>
              </select>
            </div>
            <div className="admin-form-field">
              <label>Note</label>
              <input type="text" value={form.note} onChange={e => setForm(p => ({...p, note: e.target.value}))} placeholder="Optionnel"/>
            </div>
          </div>
          <div style={{display:'flex',gap:'0.75rem',marginTop:'1rem'}}>
            <button className="admin-btn admin-btn--primary" onClick={handleAdd} disabled={saving}>
              {saving ? <span className="admin-spinner-sm"/> : 'Enregistrer'}
            </button>
            <button className="admin-btn admin-btn--secondary" onClick={() => setShowForm(false)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs" style={{marginBottom:'1rem'}}>
        {[
          ['apercu',        '📊 Aperçu global'],
          ['inscriptions',  '🎓 Inscriptions'],
          ['mensuels',      '📅 Paiements mensuels'],
        ].map(([k,l]) => (
          <button key={k} className={`admin-tab ${activeTab===k?'active':''}`} onClick={() => setActiveTab(k)}>{l}</button>
        ))}
      </div>

      {/* Recherche */}
      <div className="admin-filters" style={{marginBottom:'1rem'}}>
        <div className="admin-search" style={{flex:1,maxWidth:360}}>
          <Search size={16}/>
          <input type="text" placeholder="Rechercher un étudiant..." value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
      </div>

      {/* Tableau */}
      <div className="admin-table-box">
        <div className="admin-table-box__header">
          <h3>
            {activeTab === 'inscriptions' ? `Frais d'inscription (${filteredByTab.length})` :
             activeTab === 'mensuels'     ? `Paiements mensuels (${filteredByTab.length})` :
             `Tous les paiements (${filteredByTab.length})`}
          </h3>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Étudiant</th>
              <th>Type</th>
              <th>Montant</th>
              <th>Mode</th>
              {activeTab !== 'inscriptions' && <th>Mois</th>}
              <th>Statut</th>
              <th>Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredByTab.length === 0 && (
              <tr><td colSpan={7} style={{textAlign:'center',padding:'2rem',color:'var(--admin-muted)'}}>Aucun paiement</td></tr>
            )}
            {filteredByTab.map(p => (
              <tr key={p.id}>
                <td>
                  <strong>{p.etudiantNom}</strong>
                  <small>{p.etudiantEmail}</small>
                </td>
                <td>
                  <span className={`admin-badge ${p.type==='inscription'?'admin-badge--info':'admin-badge--niveau'}`}>
                    {p.type === 'inscription' ? '🎓 Inscription' : '📅 Mensuel'}
                  </span>
                </td>
                <td><strong>{(p.montant||0).toLocaleString('fr-FR')} FCFA</strong></td>
                <td>{p.mode || 'Wave'}</td>
                {activeTab !== 'inscriptions' && <td>{p.mois || '—'}</td>}
                <td>
                  <span className={`admin-badge ${p.statut==='payé'?'admin-badge--ok':p.statut==='en attente'?'admin-badge--warn':'admin-badge--err'}`}>
                    {p.statut}
                  </span>
                </td>
                <td style={{fontSize:'0.8rem',color:'var(--admin-muted)'}}>
                  {p.createdAt ? new Date(p.createdAt.seconds*1000).toLocaleDateString('fr-FR') : '—'}
                </td>
                {/* Boutons confirmation — IT Manager et General Manager uniquement */}
                {p.statut === 'en attente' && ['it_manager','general_manager'].includes(staff?.role) && (
                  <td>
                    <div style={{display:'flex',gap:'0.4rem'}}>
                      <button
                        className="admin-btn admin-btn--success"
                        style={{padding:'0.3rem 0.65rem',fontSize:'0.75rem'}}
                        onClick={() => handleConfirm(p)}
                      >
                        <CheckCircle size={13}/> Confirmer
                      </button>
                      <button
                        className="admin-btn admin-btn--danger"
                        style={{padding:'0.3rem 0.65rem',fontSize:'0.75rem'}}
                        onClick={() => handleReject(p)}
                      >
                        <XCircle size={13}/> Rejeter
                      </button>
                    </div>
                  </td>
                )}
                {(p.statut !== 'en attente' || !['it_manager','general_manager'].includes(staff?.role)) && (
                  <td>—</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}